import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { 
  Search, 
  CheckCircle, 
  XCircle,
  Eye,
  Clock,
  X,
  RefreshCw
} from "lucide-react";
import { paymentsAPI } from "../../services/api";

// Helper function to format date from YYYY-MM-DD to DD/M/YYYY
const formatDate = (dateString) => {
  if (!dateString) return "N/A";
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return dateString;
  const day = date.getDate();
  const month = date.getMonth() + 1;
  const year = date.getFullYear();
  return `${day}/${month}/${year}`;
};

const formatPaymentType = (type) => {
  if (!type) return "N/A";
  return type
    .replace(/_/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());
};

// Static list of payment types
const PAYMENT_TYPES = [
  { value: "all", label: "All Types" },
  { value: "new_application", label: "New Application" },
  { value: "renewal", label: "Renewal" }
];

export function PaymentsList({ filter }) {
  const [searchTerm, setSearchTerm] = useState("");
  const [paymentTypeFilter, setPaymentTypeFilter] = useState("all");
  const [viewModal, setViewModal] = useState({ show: false, payment: null });
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Pagination state
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  useEffect(() => {
    fetchPayments();
  }, [paymentTypeFilter, page, limit]);

  const fetchPayments = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const params = { page, limit };
      if (paymentTypeFilter !== "all") {
        params.paymentType = paymentTypeFilter;
      }
      
      const response = await paymentsAPI.getAll(params);
      
      if (response.success) {
        setPayments(response.data || []);
        setTotal(response.total || 0);
        setTotalPages(response.totalPages || 0);
      } else {
        setError(response.message);
      }
    } catch (err) {
      setError(err.message || "Failed to fetch payments");
      console.error("Error fetching payments:", err);
    } finally {
      setLoading(false);
    }
  };

  // Filter payments by search term
  const filteredPayments = payments.filter((payment) => {
    const matchesSearch = 
      (payment.name && payment.name.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (payment.payment_number && payment.payment_number.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (payment.transaction_id && payment.transaction_id.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (payment.utr_number && payment.utr_number.toLowerCase().includes(searchTerm.toLowerCase()));
    
    return matchesSearch;
  });

  // Handle view
  const handleView = async (payment) => {
    try {
      const response = await paymentsAPI.getById(payment.id);
      if (response.success) {
        setViewModal({ show: true, payment: response.data });
      }
    } catch (err) {
      console.error("Error fetching payment details:", err);
      setViewModal({ show: true, payment });
    }
  };

  // Close view modal
  const closeViewModal = () => {
    setViewModal({ show: false, payment: null });
  };

  // Handle verify payment
  const verifyPayment = async (id, newStatus) => {
    try {
      await paymentsAPI.verify(id, { status: newStatus });
      fetchPayments();
      // Update modal if open
      if (viewModal.show && viewModal.payment?.id === id) {
        const updated = payments.find(p => p.id === id);
        if (updated) {
          setViewModal({ ...viewModal, payment: { ...updated, status: newStatus } });
        }
      }
    } catch (err) {
      console.error("Error verifying payment:", err);
      alert("Failed to verify payment");
    }
  };

  const getStatusBadge = (status) => {
    switch(status) {
      case 'completed':
        return <span className="flex items-center gap-1 px-2 py-1 text-xs rounded-full bg-green-100 text-green-700">
          <CheckCircle className="w-3 h-3" /> Completed
        </span>;
      case 'pending':
        return <span className="flex items-center gap-1 px-2 py-1 text-xs rounded-full bg-yellow-100 text-yellow-700">
          <Clock className="w-3 h-3" /> Pending
        </span>;
      case 'failed':
        return <span className="flex items-center gap-1 px-2 py-1 text-xs rounded-full bg-red-100 text-red-700">
          <XCircle className="w-3 h-3" /> Failed
        </span>;
      default:
        return <span className="px-2 py-1 text-xs rounded-full bg-gray-100 text-gray-700">{status}</span>;
    }
  };

  // Show loading state
  if (loading) {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold text-[#111111]">
            {filter === 'pending' ? 'Pending Payments' : 'Payments Management'}
          </h1>
          <p className="text-gray-600">Track and manage all payment transactions</p>
        </div>
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <RefreshCw className="w-8 h-8 animate-spin text-[#C46A1B] mx-auto mb-2" />
            <p className="text-gray-500">Loading payments...</p>
          </div>
        </div>
      </div>
    );
  }

  // Show error state
  if (error) {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold text-[#111111]">
            {filter === 'pending' ? 'Pending Payments' : 'Payments Management'}
          </h1>
          <p className="text-gray-600">Track and manage all payment transactions</p>
        </div>
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-600">{error}</p>
          <button
            onClick={fetchPayments}
            className="mt-2 px-4 py-2 bg-[#C46A1B] text-white rounded hover:bg-[#9E4E10]"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-[#111111]">
          {filter === 'pending' ? 'Pending Payments' : 'Payments Management'}
        </h1>
        <p className="text-gray-600">Track and manage all payment transactions</p>
      </div>

      {/* Search and Filter */}
      <div className="bg-white p-4 rounded-lg shadow-sm">
        <div className="flex flex-wrap gap-4 items-center">
          {/* Search */}
          <div className="flex-1 flex items-center gap-2 bg-gray-100 px-3 py-2 rounded-lg min-w-[200px]">
            <Search className="w-4 h-4 text-gray-500" />
            <input
              type="text"
              placeholder="Search by name, payment ID, or UTR..."
              className="bg-transparent border-none outline-none text-sm w-full"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            {searchTerm && (
              <button onClick={() => setSearchTerm("")} className="p-1 hover:bg-gray-200 rounded">
                <X className="w-3 h-3" />
              </button>
            )}
          </div>

          {/* Payment Type Dropdown Filter */}
          <div className="flex items-center gap-2">
            <label className="text-sm font-medium text-gray-700">Payment Type:</label>
            <select
              className="border rounded-lg px-4 py-2 bg-white focus:ring-2 focus:ring-[#C46A1B] focus:border-transparent outline-none min-w-[180px]"
              value={paymentTypeFilter}
              onChange={(e) => setPaymentTypeFilter(e.target.value)}
            >
              {PAYMENT_TYPES.map(type => (
                <option key={type.value} value={type.value}>
                  {type.label}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Payments Table */}
      <div className="bg-white rounded-lg shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr className="text-left text-sm text-gray-600">
                <th className="p-4"> Payment No.</th>
                <th className="p-4">Name</th>
                <th className="p-4">Amount</th>
                <th className="p-4">Date</th>
                <th className="p-4">Payment Type</th>
                <th className="p-4">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {filteredPayments.map((payment) => (
                <tr key={payment.id} className="hover:bg-gray-50">
                  <td className="p-4">
                    <button
                      onClick={() => handleView(payment)}
                      className="font-mono text-sm font-medium hover:text-[#C46A1B]"
                    >
                      {payment.payment_number || "N/A"}
                    </button>
                  </td>
                  <td className="p-4">
                    <div>
                      <p className="font-medium">{payment.name || "N/A"}</p>
                      {payment.application && (
                        <p className="text-xs text-gray-500">{payment.application.application_number}</p>
                      )}
                    </div>
                  </td>
                  <td className="p-4 font-medium">₹{payment.amount}</td>
                  <td className="p-4 text-sm">{formatDate(payment.payment_date)}</td>
                  <td className="p-4 text-sm">{formatPaymentType(payment.payment_type)}</td>
                  <td className="p-4">
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => handleView(payment)}
                        className="p-1 hover:bg-gray-100 rounded"
                        title="View Details"
                      >
                        <Eye className="w-4 h-4 text-gray-600" />
                      </button>
                      {payment.status === 'pending' && (
                        <button
                          onClick={() => verifyPayment(payment.id, 'completed')}
                          className="p-1 hover:bg-green-100 rounded"
                          title="Verify Payment"
                        >
                          <CheckCircle className="w-4 h-4 text-green-600" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {filteredPayments.length === 0 && (
          <div className="p-12 text-center text-gray-500">
            <p className="text-lg">No payments found</p>
            <p className="text-sm mt-1">Try adjusting your search or filters</p>
          </div>
        )}

        {/* Pagination */}
        <div className="p-4 border-t flex justify-between items-center">
          <p className="text-sm text-gray-600">
            Showing {filteredPayments.length} of {total} payments
          </p>
          <div className="flex gap-2">
            <button 
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={page === 1}
              className="px-3 py-1 border rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Previous
            </button>
            <button className="px-3 py-1 bg-[#C46A1B] text-white rounded hover:bg-[#9E4E10]">
              {page}
            </button>
            <button 
              onClick={() => setPage(p => Math.min(totalPages, p + 1))}
              disabled={page >= totalPages}
              className="px-3 py-1 border rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Next
            </button>
          </div>
        </div>
      </div>

      {/* View Payment Modal - Full screen overlay with inline styles */}
      {viewModal.show && viewModal.payment && (
        <div 
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 50,
            padding: 0,
            margin: 0
          }}
          onClick={closeViewModal}
        >
          <div 
            className="bg-white rounded-lg shadow-xl max-w-2xl w-full"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-6 border-b flex justify-between items-center">
              <h3 className="text-lg font-semibold text-[#111111]">Payment Details</h3>
              <button 
                onClick={closeViewModal}
                className="p-2 hover:bg-gray-100 rounded-lg"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="p-6 space-y-4">
              {/* Transaction Info */}
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-gray-50 p-3 rounded-lg">
                  <p className="text-xs text-gray-500"> Payment Number</p>
                  <p className="font-mono font-medium">{viewModal.payment.payment_number || "N/A"}</p>
                </div>
                <div className="bg-gray-50 p-3 rounded-lg">
                  <p className="text-xs text-gray-500">Amount</p>
                  <p className="text-xl font-bold text-[#C46A1B]">₹{viewModal.payment.amount}</p>
                </div>
                <div className="bg-gray-50 p-3 rounded-lg">
                  <p className="text-xs text-gray-500">Name</p>
                  <p className="font-medium">{viewModal.payment.name || "N/A"}</p>
                </div>
                <div className="bg-gray-50 p-3 rounded-lg">
                  <p className="text-xs text-gray-500">Payment Type</p>
                  <p className="font-medium">{formatPaymentType(viewModal.payment.payment_type)}</p>
                </div>
                <div className="bg-gray-50 p-3 rounded-lg">
                  <p className="text-xs text-gray-500">Payment Method</p>
                  <p className="font-medium capitalize">{viewModal.payment.payment_method || "N/A"}</p>
                </div>
                <div className="bg-gray-50 p-3 rounded-lg">
                  <p className="text-xs text-gray-500">Payment Date</p>
                  <p className="font-medium">{formatDate(viewModal.payment.payment_date)}</p>
                </div>
              </div>

              {/* Transaction ID / UTR */}
              {viewModal.payment.transaction_id && (
                <div className="bg-gray-50 p-3 rounded-lg">
                  <p className="text-xs text-gray-500">Transaction ID</p>
                  <p className="font-mono font-medium">{viewModal.payment.transaction_id}</p>
                </div>
              )}
              {viewModal.payment.utr_number && (
                <div className="bg-gray-50 p-3 rounded-lg">
                  <p className="text-xs text-gray-500">UTR Number</p>
                  <p className="font-mono font-medium">{viewModal.payment.utr_number}</p>
                </div>
              )}

              {/* Status */}
              <div className="flex items-center gap-2">
                <p className="text-sm text-gray-600">Status:</p>
                {getStatusBadge(viewModal.payment.status)}
              </div>

              {/* Application Info */}
              {viewModal.payment.application && (
                <div className="bg-gray-50 p-3 rounded-lg">
                  <p className="text-xs text-gray-500 mb-2">Related Application</p>
                  <p className="font-medium">{viewModal.payment.application.full_name}</p>
                  <p className="text-sm text-gray-500">{viewModal.payment.application.application_number}</p>
                </div>
              )}
            </div>

            <div className="p-6 border-t bg-gray-50 flex justify-end gap-3">
              {viewModal.payment.status === 'pending' && (
                <>
                  <button
                    onClick={() => {
                      verifyPayment(viewModal.payment.id, 'failed');
                      closeViewModal();
                    }}
                    className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition"
                  >
                    Mark as Failed
                  </button>
                  <button
                    onClick={() => {
                      verifyPayment(viewModal.payment.id, 'completed');
                      closeViewModal();
                    }}
                    className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition"
                  >
                    Verify Payment
                  </button>
                </>
              )}
              <button
                onClick={closeViewModal}
                className="px-4 py-2 border rounded-lg hover:bg-white transition"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
